import { MEMBER_CATEGORY } from '@/constants';
import * as accountService from '@/services/account';
import axios from 'axios';
import { matchesSearchQuery } from '../utilities/manage-members-helpers';

export default {
  namespaced: false,

  state: {
    selectedCategory: 'members', // members, admins, pending
    myTeam: {
      team_memberships: [],
      links: {},
      meta: {},
      seats: {
        total: 0,
        empty: 0,
      },
    },
    teamManagementLoading: false,
    teamManagementError: null,
    query: null,
    additionalMembers: {
      owner_email: '',
      invite_emails: [],
      prorated_amount: 0,
      proration_date: 0,
      days_until_renewal: 0,
    },
  },
  getters: {
    selectedCategory(state) {
      return state.selectedCategory;
    },
    myTeam(state) {
      return state.myTeam;
    },
    activeMembers(state) {
      const members = state.myTeam.team_memberships;
      const { query } = state;

      return members.filter(
        member => member.team_membership_status_status.name.toLowerCase() === 'active'
          && (matchesSearchQuery(member.email, query)
            || matchesSearchQuery(member.name, query)),
      );
    },
    activeAdminMembers: (state, getter) => {
      const { activeMembers } = getter;
      const { query } = state;

      return activeMembers.filter(
        member => member.team_roles.filter(
          team_role => team_role.name.toLowerCase() === 'admin'
              && (matchesSearchQuery(member.email, query)
                || matchesSearchQuery(member.name, query)),
        ).length > 0,
      );
    },
    pendingMembers: (state) => {
      const members = state.myTeam.team_memberships;
      const { query } = state;

      return members.filter(
        member => member.team_membership_status_status.name.toLowerCase() === 'pending'
          && (matchesSearchQuery(member.email, query)
            || matchesSearchQuery(member.name, query)),
      );
    },
    selectedCategoryMembers: (state, getter) => {
      const { selectedCategory } = state;
      const { activeMembers, activeAdminMembers, pendingMembers } = getter;

      switch (selectedCategory) {
        case MEMBER_CATEGORY.MEMBERS: {
          return activeMembers;
        }

        case MEMBER_CATEGORY.ADMINS: {
          return activeAdminMembers;
        }

        case MEMBER_CATEGORY.PENDING: {
          return pendingMembers;
        }

        default:
          return activeMembers;
      }
    },
    categoryMembersCount: (state, getter) => (category) => {
      const { activeMembers, activeAdminMembers, pendingMembers } = getter;

      switch (category) {
        case MEMBER_CATEGORY.MEMBERS: {
          return activeMembers.length;
        }

        case MEMBER_CATEGORY.ADMINS: {
          return activeAdminMembers.length;
        }

        case MEMBER_CATEGORY.PENDING: {
          return pendingMembers.length;
        }
        default:
          return null;
      }
    },
    teamManagementLoading(state) {
      return state.teamManagementLoading;
    },
    additionalMembers(state) {
      return state.additionalMembers;
    },
    query(state) {
      return state.query;
    },
    teamManagementError(state) {
      return state.teamManagementError;
    },
  },
  mutations: {
    SET_SELECTED_CATEGORY: (state, payload) => {
      state.selectedCategory = payload;
    },
    SET_LOADING: (state, payload) => {
      state.teamManagementLoading = payload;
    },
    SET_ERROR: (state, payload) => {
      state.teamManagementError = payload;
    },
    SET_MY_TEAM: (state, payload) => {
      state.myTeam = { ...state.myTeam, ...payload };
    },
    SET_ADDITIONAL_MEMBERS(state, payload) {
      state.additionalMembers = { ...state.additionalMembers, ...payload };
    },
    SET_QUERY: (state, payload) => {
      state.query = payload;
    },
    SET_TEAM: (state, payload) => {
      state.team = { ...payload };
      state.loading.team = false;
    },
  },
  actions: {
    setSelectedCategory({ commit }, payload) {
      commit('SET_SELECTED_CATEGORY', payload);
    },
    setMyTeam({ commit }, payload) {
      commit('SET_MY_TEAM', payload);
    },
    setTeamManagementLoading({ commit }, payload) {
      commit('SET_LOADING', payload);
    },
    setTeamManagementError({ commit }, payload) {
      commit('SET_ERROR', payload);
    },
    getMembers: async ({ dispatch, commit }) => {
      try {
        dispatch('setTeamManagementError', null);
        dispatch('setTeamManagementLoading', true);
        const { data } = await accountService.getMyTeamMemberships();
        dispatch('setMyTeam', {
          team_memberships: data.data,
          links: data.links,
          meta: data.meta,
        });
      } catch (error) {
        dispatch('setTeamManagementError', error);
        return null;
      }
      dispatch('setTeamManagementLoading', false);
    },
    editAction: async ({ dispatch }, payload) => {
      try {
        dispatch('setTeamManagementError', null);
        dispatch('setTeamManagementLoading', true);
        const { method, url } = payload;

        const result = await axios({ method, url });

        const { data } = result;
        if (data && data.data) {
          dispatch('setMyTeam', data.data);
        }
      } catch (error) {
        dispatch('setTeamManagementError', error);
        return null;
      }
      dispatch('setTeamManagementLoading', false);
    },
    setAdditionalMembers({ commit }, payload) {
      commit('SET_ADDITIONAL_MEMBERS', payload);
    },
    calculateProrateAmount: async ({ dispatch }, payload) => {
      try {
        const { data } = await accountService.calculateProrateAmount(payload);
        dispatch('setAdditionalMembers', data);
        return data;
      } catch (error) {
        dispatch('setTeamManagementError', error);
        return null;
      }
    },
    setQuery({ commit }, payload) {
      commit('SET_QUERY', payload);
    },
    getMyTeam: async ({ dispatch }) => {
      try {
        const { data } = await accountService.getMyTeam();
        dispatch('setMyTeam', data.data);
        return data;
      } catch (error) {
        dispatch('setTeamManagementError', error);
        return null;
      }
    },
    updateSeats: async ({ dispatch }, payload) => {
      try {
        dispatch('setTeamManagementError', null);
        dispatch('setTeamManagementLoading', true);
        const { data } = await accountService.updateTeamSeats(payload);
        if (data && data.data) {
          dispatch('setMyTeam', data.data);
        }
        dispatch('setTeamManagementLoading', false);
        return data;
      } catch (error) {
        dispatch('setTeamManagementError', error);
        dispatch('setTeamManagementLoading', false);
        return null;
      }
    },
    inviteMembers: async ({ dispatch }, payload) => {
      try {
        dispatch('setTeamManagementError', null);
        dispatch('setTeamManagementLoading', true);
        const { data } = await accountService.inviteMemberToMyTeam(payload);
        if (data && data.data) {
          dispatch('setMyTeam', data.data);
        }
        dispatch('setTeamManagementLoading', false);
        return data;
      } catch (error) {
        dispatch('setTeamManagementError', error);
        dispatch('setTeamManagementLoading', false);
        return null;
      }
    },
  },
};
